import { reconcile, verifyVerbatim, type NormalizedTransaction, type NormalizedStatement, type TransactionKind } from "@paredown/shared";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { money, type Api, type StatementDetail } from "../api.js";
import { labelFor } from "../vault.js";

const KINDS: TransactionKind[] = ["purchase", "payment", "credit", "fee", "interest", "transfer", "other"];

/** Edit cents as dollars in the table. */
const toDollars = (c: number) => (c / 100).toFixed(2);
const toCents = (s: string) => Math.round(Number(s.replace(/[$,]/g, "")) * 100);

export function Confirm({ api }: { api: Api }) {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState<StatementDetail | null>(null);
  const [draft, setDraft] = useState<NormalizedStatement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Only the rows a check flagged are on the critical path; the rest are one click away. */
  const [showAllRows, setShowAllRows] = useState(false);
  /** A confirmed statement opens as plain text; Edit turns the cells back into fields. */
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api
      .getStatement(id)
      .then((d) => {
        setDoc(d);
        setDraft(d.confirmed ?? d.extracted ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, [api, id]);

  // Guards re-run live on the draft so the user sees problems clear as they fix cells.
  const problems = useMemo(() => {
    if (!draft || !doc) return { rows: new Map<number, string[]>(), reconcile: [] as string[] };
    const rows = new Map<number, string[]>();
    for (const p of verifyVerbatim(draft, doc.redactedText)) rows.set(p.row, [...(rows.get(p.row) ?? []), p.message]);
    return { rows, reconcile: reconcile(draft).problems };
  }, [draft, doc]);

  const computed = useMemo(() => {
    const sum = (kinds: TransactionKind[]) =>
      draft?.transactions.filter((t) => kinds.includes(t.kind)).reduce((a, t) => a + t.amountCents, 0) ?? 0;
    return {
      purchasesCents: sum(["purchase"]),
      paymentsAndCreditsCents: sum(["payment", "credit"]),
      feesCents: sum(["fee"]),
      interestCents: sum(["interest"]),
    };
  }, [draft]);

  if (error) return <p className="error">{error}</p>;
  if (!doc || !draft) return <p>Loading…</p>;

  const setRow = (i: number, patch: Partial<NormalizedTransaction>) =>
    setDraft({ ...draft, transactions: draft.transactions.map((t, k) => (k === i ? { ...t, ...patch } : t)) });
  const dropRow = (i: number) => setDraft({ ...draft, transactions: draft.transactions.filter((_, k) => k !== i) });
  const clean = problems.rows.size === 0 && problems.reconcile.length === 0;
  const confirmed = doc.status === "confirmed";
  const readOnly = confirmed && !editing;
  const flaggedOnly = !readOnly && !showAllRows && problems.rows.size > 0;
  const shownRows = draft.transactions.map((t, i) => [t, i] as const).filter(([, i]) => !flaggedOnly || problems.rows.has(i));

  async function save() {
    setSaving(true);
    try {
      const summary = await api.confirm(id, draft!);
      if (confirmed) {
        // Stay on the page: the user came here to fix something and may want to see it saved.
        setDoc({ ...doc!, ...summary, confirmed: draft!, autoConfirmed: false });
        setEditing(false);
        setSaving(false);
      } else {
        nav("/statements");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  function cancelEdit() {
    setDraft(doc!.confirmed ?? doc!.extracted ?? draft);
    setEditing(false);
  }

  return (
    <section className="confirm">
      <p>
        <Link to="/statements">← Statements</Link>
      </p>
      <h2>
        {confirmed ? (editing ? "Editing" : doc.autoConfirmed ? "Checks passed" : "Confirmed") : "Check the flagged rows"}: {doc.fileName}
      </h2>
      <p>
        {draft.issuer} · {draft.accountKind.replace("_", " ")} · {labelFor(draft.accountToken)} · {draft.periodStart} → {draft.periodEnd}
      </p>

      <div className="split">
        <div className="pdf">
          {doc.pdfUrl ? (
            <iframe title="Redacted statement" src={doc.pdfUrl} />
          ) : (
            <div className="pdf-missing" role="status" data-testid="pdf-missing">
              <p>
                <strong>The PDF for this statement is no longer stored.</strong>
              </p>
              <p className="muted small">
                The rows read from it are still here and still count toward your recurring charges. To see the PDF again, upload
                the same file on the <Link to="/statements">Statements</Link> page; it reattaches to this statement without
                re-reading it.
              </p>
            </div>
          )}
        </div>
        <div className="rows">
          {readOnly && (
            <p>
              <button type="button" onClick={() => setEditing(true)} data-testid="edit-button">
                Edit
              </button>{" "}
              <span className="muted small">Fix a date, amount or kind. Saving re-runs the checks and updates your recurring charges.</span>
            </p>
          )}
          {!readOnly && problems.rows.size > 0 && (
            <p>
              {flaggedOnly
                ? `Showing the ${problems.rows.size} flagged row${problems.rows.size === 1 ? "" : "s"} of ${draft.transactions.length}. `
                : `Showing all ${draft.transactions.length} rows. `}
              <button type="button" className="link" onClick={() => setShowAllRows(!showAllRows)} data-testid="toggle-rows">
                {flaggedOnly ? "Show all rows" : "Show only flagged rows"}
              </button>
            </p>
          )}
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Kind</th>
                {!readOnly && <th />}
              </tr>
            </thead>
            <tbody>
              {shownRows.map(([t, i]) => {
                const issues = problems.rows.get(i);
                return (
                  <Fragment key={i}>
                  <tr className={issues ? "flagged" : ""} data-testid="txn-row">
                    {readOnly ? (
                      <>
                        <td>{t.date}</td>
                        <td>{t.rawDescription}</td>
                        <td className="num">{toDollars(t.amountCents)}</td>
                        <td>{t.kind}</td>
                      </>
                    ) : (
                      <>
                        <td>
                          <input value={t.date} onChange={(e) => setRow(i, { date: e.target.value })} size={10} aria-label="date" />
                        </td>
                        <td>
                          <input value={t.rawDescription} onChange={(e) => setRow(i, { rawDescription: e.target.value })} aria-label="description" />
                        </td>
                        <td>
                          <input
                            defaultValue={toDollars(t.amountCents)}
                            onBlur={(e) => {
                              const c = toCents(e.target.value);
                              if (Number.isFinite(c)) setRow(i, { amountCents: c });
                            }}
                            size={9}
                            className="num"
                            aria-label="amount"
                          />
                        </td>
                        <td>
                          <select value={t.kind} onChange={(e) => setRow(i, { kind: e.target.value as TransactionKind })} aria-label="kind">
                            {KINDS.map((k) => (
                              <option key={k}>{k}</option>
                            ))}
                          </select>
                        </td>
                      </>
                    )}
                    {!readOnly && (
                      <td>
                        <button type="button" className="link danger" onClick={() => dropRow(i)} aria-label="drop row">
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                  {issues && (
                    <tr className="flagged why" data-testid="txn-why">
                      <td colSpan={readOnly ? 4 : 5}>
                        {issues.map((m) => (
                          <div key={m}>{m}</div>
                        ))}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <h3>Totals</h3>
          <table className="totals">
            <thead>
              <tr>
                <th />
                <th>Printed</th>
                <th>Rows add up to</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["Purchases", "purchasesCents"],
                  ["Payments and credits", "paymentsAndCreditsCents"],
                  ["Fees", "feesCents"],
                  ["Interest", "interestCents"],
                ] as const
              ).map(([label, k]) => {
                const printed = draft.printedTotals[k];
                const ok = printed === undefined || printed === computed[k];
                return (
                  <tr key={k} className={ok ? "" : "flagged"}>
                    <td>{label}</td>
                    <td className="num">{printed === undefined ? "not printed" : money(printed)}</td>
                    <td className="num">{money(computed[k])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {problems.reconcile.length > 0 && (
            <ul className="error">
              {problems.reconcile.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}

          {!readOnly && (
            <div className="confirm-bar">
              <p>
                {clean
                  ? "Every check passed. Nothing is saved until you confirm."
                  : `${problems.rows.size} row${problems.rows.size === 1 ? "" : "s"} and ${problems.reconcile.length} total${problems.reconcile.length === 1 ? "" : "s"} need a look; the reason is shown under each. You can still confirm as is.`}
              </p>
              <button type="button" onClick={save} disabled={saving} data-testid="confirm-button">
                {saving ? "Saving…" : confirmed ? "Save" : clean ? "Approve" : "Confirm anyway"}
              </button>
              {confirmed && (
                <button type="button" className="link" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
